import {
  DestroyRef,
  Injectable,
  InjectionToken,
  inject,
  signal,
} from '@angular/core';
import {
  AuthSession,
  fetchAuthSession,
  signInWithRedirect,
  signOut,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { Profile } from '../models/profile';

export const AUTH_CLIENT = new InjectionToken('AUTH_CLIENT', {
  providedIn: 'root',
  factory: () => ({ fetchAuthSession, signInWithRedirect, signOut }),
});

export interface SessionTokens {
  idToken: string;
  accessToken: string;
}

export type AuthStatus =
  | 'loading'
  | 'authenticated'
  | 'unauthenticated'
  | 'error';

const SIGNED_OUT_KEY = 'auth:signed-out';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly client = inject(AUTH_CLIENT);
  private readonly userState = signal<Profile | null>(null);
  private readonly statusState = signal<AuthStatus>('loading');
  private readonly signedOutState = signal(
    sessionStorage.getItem(SIGNED_OUT_KEY) === 'true',
  );
  private pendingSession: Promise<Profile | null> | undefined;
  private sessionVersion = 0;

  readonly user = this.userState.asReadonly();
  readonly status = this.statusState.asReadonly();
  readonly explicitlySignedOut = this.signedOutState.asReadonly();

  constructor() {
    localStorage.removeItem('azUser');
    const stopListening = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedOut') {
        this.clearSession();
      } else if (
        payload.event === 'tokenRefresh' &&
        this.pendingSession === undefined
      ) {
        void this.initializeSession().catch(() => undefined);
      } else if (payload.event === 'tokenRefresh_failure') {
        this.clearSession();
        this.statusState.set('error');
      }
    });
    inject(DestroyRef).onDestroy(stopListening);
  }

  initializeSession(): Promise<Profile | null> {
    if (this.explicitlySignedOut()) {
      this.clearSession();
      return Promise.resolve(null);
    }
    if (this.pendingSession === undefined) {
      this.statusState.set('loading');
      const version = this.sessionVersion;
      const pending = this.client
        .fetchAuthSession()
        .then((session) =>
          version === this.sessionVersion ? this.updateSession(session) : null,
        )
        .catch(() => {
          if (version === this.sessionVersion) {
            this.clearSession();
            this.statusState.set('error');
          }
          throw new Error('Unable to load the authentication session.');
        });
      this.pendingSession = pending;
      const cleanup = () => {
        if (this.pendingSession === pending) {
          this.pendingSession = undefined;
        }
      };
      pending.then(cleanup, cleanup);
    }
    return this.pendingSession;
  }

  async signIn(): Promise<void> {
    sessionStorage.removeItem(SIGNED_OUT_KEY);
    this.signedOutState.set(false);
    await this.client.signInWithRedirect({ provider: { custom: 'AzureAD' } });
  }

  async signOut(): Promise<void> {
    sessionStorage.setItem(SIGNED_OUT_KEY, 'true');
    this.signedOutState.set(true);
    this.clearSession();
    this.statusState.set('loading');
    try {
      await this.client.signOut();
    } finally {
      this.clearSession();
    }
  }

  async getTokens(): Promise<SessionTokens> {
    if (this.explicitlySignedOut()) {
      throw new Error('Authentication required.');
    }
    const version = this.sessionVersion;
    try {
      const session = await this.client.fetchAuthSession();
      const tokens = session.tokens;
      if (
        version !== this.sessionVersion ||
        !tokens?.idToken ||
        !this.updateSession(session)
      ) {
        throw new Error('Authentication required.');
      }
      return {
        idToken: tokens.idToken.toString(),
        accessToken: tokens.accessToken.toString(),
      };
    } catch {
      if (version === this.sessionVersion) {
        this.clearSession();
        this.statusState.set('error');
      }
      throw new Error('Authentication required.');
    }
  }

  private clearSession(): void {
    this.sessionVersion++;
    this.pendingSession = undefined;
    this.userState.set(null);
    this.statusState.set('unauthenticated');
  }

  private updateSession(session: AuthSession): Profile | null {
    const tokens = session.tokens;
    const now = Math.floor(Date.now() / 1000);
    if (
      !tokens?.idToken ||
      !tokens.accessToken ||
      !tokens.idToken.payload.exp ||
      tokens.idToken.payload.exp <= now ||
      !tokens.accessToken.payload.exp ||
      tokens.accessToken.payload.exp <= now
    ) {
      this.clearSession();
      return null;
    }

    const claims = tokens.idToken.payload;
    const text = (claim: unknown): string =>
      typeof claim === 'string' ? claim : '';
    const givenName = text(claims['given_name']);
    const familyName =
      text(claims['family_name']) || text(claims['middle_name']);
    const email = text(claims['email']);
    const profile: Profile = {
      userId: text(claims.sub),
      displayName:
        text(claims['name']) ||
        [givenName, familyName].filter(Boolean).join(' '),
      prid: text(claims['custom:prid']),
      email,
      userPrincipalName: text(claims['custom:upn']) || email,
      givenName,
      familyName,
      groups: this.parseGroups(claims['custom:adgroups']),
      cognitoGroups: this.parseGroups(claims['cognito:groups']),
      lastUpdate: new Date().toISOString(),
    };
    this.userState.set(profile);
    this.statusState.set('authenticated');
    return profile;
  }

  private parseGroups(claim: unknown): string[] {
    if (Array.isArray(claim)) {
      return claim.filter(
        (group): group is string => typeof group === 'string',
      );
    }
    if (typeof claim !== 'string' || !claim.trim()) {
      return [];
    }
    const value = claim.trim();
    if (value.startsWith('[') && value.endsWith(']')) {
      try {
        const parsed: unknown = JSON.parse(value);
        return Array.isArray(parsed) ? this.parseGroups(parsed) : [];
      } catch {
        return value
          .slice(1, -1)
          .split(',')
          .map((group) => group.trim())
          .filter(Boolean);
      }
    }
    return value.split(/[,\s]+/).filter(Boolean);
  }
}
